import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Package, Warning, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import AttachmentsButton from "@/components/AttachmentsButton";
import { usePermissions } from "@/lib/permissions";

export default function Products() {
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [f, setF] = useState({ name: "", sku: "", price: 0, dealer_price: 0, stock: 0, category: "", brand: "", gst: 18, low_stock_alert: 5 });

  const load = async () => { const r = await api.get("/products"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.name || !f.sku) return toast.error("Name & SKU required");
    const body = { ...f, price: +f.price, dealer_price: +f.dealer_price, stock: +f.stock, gst: +f.gst, low_stock_alert: +f.low_stock_alert };
    try {
      if (editingId) { await api.put(`/products/${editingId}`, body); toast.success("Product updated"); }
      else { await api.post("/products", body); toast.success("Product added"); }
      setOpen(false); setEditingId(null); load();
      setF({ name: "", sku: "", price: 0, dealer_price: 0, stock: 0, category: "", brand: "", gst: 18, low_stock_alert: 5 });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEdit = (p) => { setF({ ...p }); setEditingId(p.id); setOpen(true); };
  const remove = async (p) => {
    if (!confirm(`Delete "${p.name}"? It will move to the recycle bin.`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete not allowed"); }
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex justify-between items-end gap-4">
        <div>
          <div className="overline text-muted-foreground mb-2">Catalogue</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">Products & Inventory</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
          {can("inventory.create") && (
            <DialogTrigger asChild><Button className="rounded-full" data-testid="add-product-button"><Plus size={16} className="mr-2" />New Product</Button></DialogTrigger>
          )}
          <DialogContent>
            <DialogHeader><DialogTitle>{editingId ? "Edit product" : "New Product"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              {[["name","Name *",1],["sku","SKU *",1],["category","Category",1],["brand","Brand",1],["price","Price ₹",1],["dealer_price","Dealer ₹",1],["stock","Stock",1],["low_stock_alert","Low Stock @",1],["gst","GST %",1]].map(([k, l]) => (
                <div key={k}>
                  <Label className="text-xs">{l.toUpperCase()}</Label>
                  <Input value={f[k]} onChange={(e) => setF({...f,[k]:e.target.value})} data-testid={`prod-${k}`} />
                </div>
              ))}
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-product-button">{editingId ? "Save changes" : "Create"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map((p) => (
          <Card key={p.id} className="p-5 hover-lift" data-testid={`product-${p.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Package size={18} weight="duotone" className="text-primary" />
              </div>
              {p.stock <= p.low_stock_alert && (
                <span className="text-xs px-2 py-1 rounded-full bg-destructive/10 text-destructive flex items-center gap-1">
                  <Warning size={12} weight="fill" /> Low
                </span>
              )}
            </div>
            <div className="font-heading font-bold leading-tight">{p.name}</div>
            <div className="text-xs text-muted-foreground font-mono mt-1">{p.sku}</div>
            <div className="flex items-baseline justify-between mt-4">
              <div>
                <div className="font-heading font-bold text-xl">₹{p.price?.toLocaleString("en-IN")}</div>
                <div className="text-xs text-muted-foreground">Dealer: ₹{p.dealer_price?.toLocaleString("en-IN")}</div>
              </div>
              <div className="text-right">
                <div className="overline text-muted-foreground text-[10px]">Stock</div>
                <div className="font-heading font-bold">{p.stock}</div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-border/40 flex justify-between items-center text-xs text-muted-foreground">
              <span>{p.category}</span>
              <div className="flex items-center gap-1">
                <span className="mr-1">{p.brand}</span>
                <AttachmentsButton entityType="product" entityId={p.id} label={`Images — ${p.name}`} testId={`attach-product-${p.id}`} />
                {can("inventory.edit") && (
                  <button onClick={() => openEdit(p)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Edit" data-testid={`edit-product-${p.id}`}><PencilSimple size={14} weight="duotone" /></button>
                )}
                {can("inventory.delete") && (
                  <button onClick={() => remove(p)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title="Delete" data-testid={`delete-product-${p.id}`}><Trash size={14} weight="duotone" /></button>
                )}
              </div>
            </div>
          </Card>
        ))}
        {!items.length && <Card className="col-span-full p-10 text-center text-muted-foreground">No products yet.</Card>}
      </div>
    </div>
  );
}
